import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { useListsRetrieval } from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { MilestoneIcon } from "@common/milestoneIcon";
import { EventIcon } from "@common/eventIcon";
import { useModal } from "@root/client/modal";
import { ListIcon } from "./listIcon";
import { TaskIcon } from "./taskIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function CreationMenu({ project }: Props) {
  const { t } = useTranslation();
  const filter = { limit: 999999999999, start: 0 };
  const { pushView } = useModal();
  const {
    lists,
    retrieveLists,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
  } = useListsRetrieval(project, filter);

  React.useEffect(() => {
    !!project.uuid &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveLists({
        project,
        filter,
      });
  }, [project.uuid]);

  return (
    <Safeguard
      useCases={[
        UseCases.createList,
        UseCases.createTask,
        UseCases.createMilestone,
        UseCases.createEvent,
      ]}
    >
      {({
        canCreateTask,
        canCreateEvent,
        canCreateList,
        canCreateMilestone,
      }) =>
        canCreateTask ||
        canCreateEvent ||
        canCreateList ||
        canCreateMilestone ? (
          <VisibilityWatcher className={styles.creationMenu}>
            {(isVisible, show, hide) => (
              <React.Fragment>
                <button
                  onClick={isVisible ? hide : show}
                  className={styles.button}
                >
                  <div className={styles.iconBox}>+</div>
                  <p>{t("projectPage.elementCreationMenu.button")}</p>
                </button>
                {isVisible && (
                  <div className={styles.menu}>
                    {canCreateList && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createList,
                            data: { project, list: lists },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <ListIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("projectPage.elementCreationMenu.createList")}
                        </p>
                      </div>
                    )}
                    {canCreateTask && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createTask,
                            data: { project, list: lists },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <TaskIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("projectPage.elementCreationMenu.createTask")}
                        </p>
                      </div>
                    )}
                    {canCreateMilestone && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createMilestone,
                            data: { project, list: lists },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <MilestoneIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("projectPage.elementCreationMenu.createMilestone")}
                        </p>
                      </div>
                    )}
                    {canCreateEvent && (
                      <div
                        className={styles.menuItem}
                        onClick={() =>
                          pushView({
                            key: UseCases.createEvent,
                            data: { project, list: lists },
                          })
                        }
                      >
                        <div className={styles.iconBox}>
                          <EventIcon className={styles.icon} />
                        </div>
                        <p className={styles.menuText}>
                          {t("projectPage.elementCreationMenu.createEvent")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )}
          </VisibilityWatcher>
        ) : (
          <React.Fragment />
        )
      }
    </Safeguard>
  );
}

export { CreationMenu };
